import React from 'react';

const Tokenomics = () => {
  // Token distribution data
  const tokenDistribution = [
    { name: "Device Income Mapping Pool", percentage: 40, color: "var(--primary)", amount: "400M AFI", description: "Device Income Mapping Pool (400M AFI)" },
    { name: "Staking Rewards Pool", percentage: 20, color: "var(--secondary)", amount: "200M AFI", description: "Staking Rewards Pool (200M AFI)" },
    { name: "Ecosystem Development Fund", percentage: 15, color: "var(--accent)", amount: "150M AFI", description: "Ecosystem Development Fund (150M AFI)" },
    { name: "Team Allocation", percentage: 10, color: "#f472b6", amount: "100M AFI", description: "Team Allocation (100M AFI)", note: "18-month linear vesting" },
    { name: "Marketing", percentage: 10, color: "#a3e635", amount: "100M AFI", description: "Marketing (100M AFI)" },
    { name: "Community Rewards", percentage: 5, color: "#fb923c", amount: "50M AFI", description: "Community Rewards (50M AFI)" }
  ];

  // SVG Donut Chart Generator
  const DonutChart = ({ data, size = 300 }) => {
    const radius = size / 2;
    const donutWidth = size * 0.267; // ~80px on a 300px chart
    const innerRadius = radius - donutWidth;
    
    // Calculate all the slices
    let currentAngle = 0;
    const slices = data.map(item => {
      // Calculate angles and position for this slice
      const sliceAngle = (item.percentage / 100) * 360;
      const endAngle = currentAngle + sliceAngle;
      
      // Convert angles to radians for calculation
      const startRad = (currentAngle - 90) * Math.PI / 180; // -90 to start at the top
      const endRad = (endAngle - 90) * Math.PI / 180;
      
      // Calculate the outer arc points
      const x1 = radius + radius * Math.cos(startRad);
      const y1 = radius + radius * Math.sin(startRad);
      const x2 = radius + radius * Math.cos(endRad);
      const y2 = radius + radius * Math.sin(endRad);
      
      // Calculate the inner arc points
      const x3 = radius + innerRadius * Math.cos(endRad);
      const y3 = radius + innerRadius * Math.sin(endRad);
      const x4 = radius + innerRadius * Math.cos(startRad);
      const y4 = radius + innerRadius * Math.sin(startRad);
      
      // Determine if the slice is more than half the circle (for arc flag)
      const largeArcFlag = sliceAngle > 180 ? 1 : 0;
      
      // Create the path element
      const path = `
        M ${x1.toFixed(2)} ${y1.toFixed(2)}
        A ${radius} ${radius} 0 ${largeArcFlag} 1 ${x2.toFixed(2)} ${y2.toFixed(2)}
        L ${x3.toFixed(2)} ${y3.toFixed(2)}
        A ${innerRadius} ${innerRadius} 0 ${largeArcFlag} 0 ${x4.toFixed(2)} ${y4.toFixed(2)}
        Z
      `;
      
      // Calculate position for the percentage label
      // Use the middle angle of the slice to position the text
      const midAngle = currentAngle + (sliceAngle / 2);
      const midRad = (midAngle - 90) * Math.PI / 180;
      // Position the label at a distance between the inner and outer radius
      const labelRadius = innerRadius + (donutWidth / 2);
      const labelX = radius + labelRadius * Math.cos(midRad);
      const labelY = radius + labelRadius * Math.sin(midRad);
      
      // Update current angle for next slice
      currentAngle = endAngle;
      
      return {
        path,
        labelX: labelX.toFixed(2),
        labelY: labelY.toFixed(2),
        color: item.color,
        percentage: item.percentage
      };
    });

    return (
      <svg width={size} height={size} viewBox={`0 0 ${size} ${size}`} className="donut-chart-svg">
        {slices.map((slice, index) => (
          <React.Fragment key={index}>
            <path 
              d={slice.path} 
              fill={slice.color} 
              className="chart-slice-path"
            />
            <text 
              x={slice.labelX} 
              y={slice.labelY} 
              textAnchor="middle" 
              dominantBaseline="middle" 
              fill="white" 
              fontWeight="bold"
              fontSize="16"
              className="chart-slice-label"
            >
              {slice.percentage}%
            </text>
          </React.Fragment>
        ))}
        {/* Center circle with total */}
        <circle cx={radius} cy={radius} r={innerRadius} fill="var(--darker)" className="chart-center-circle" />
        <text x={radius} y={radius - 10} textAnchor="middle" fill="white" fontWeight="bold" fontSize="20" className="chart-center-label-1">1B</text>
        <text x={radius} y={radius + 15} textAnchor="middle" fill="var(--text-secondary)" fontSize="16" className="chart-center-label-2">AFI</text>
      </svg>
    );
  };

  // Emission schedule data
  const emissionData = [
    { year: "Year 1", percentage: 30, total: "120,000,000 AFI", short: "120M", daily: "328,767 AFI", height: 100, dailyTooltip: "Requires approximately 13,151 USDT in daily sales to reach maximum emission" },
    { year: "Year 2", percentage: 25, total: "100,000,000 AFI", short: "100M", daily: "273,973 AFI", height: 83 },
    { year: "Year 3", percentage: 20, total: "80,000,000 AFI", short: "80M", daily: "219,178 AFI", height: 66 },
    { year: "Year 4", percentage: 15, total: "60,000,000 AFI", short: "60M", daily: "164,384 AFI", height: 50 },
    { year: "Year 5+", percentage: 10, total: "40,000,000 AFI", short: "40M", daily: "Declining", height: 33 }
  ];

  // Responsive Emission Schedule Component
  const EmissionSchedule = () => {
    return (
      <div className="emission-schedule">
        <h4 className="emission-heading">Mining Reward Emission Schedule</h4>
        
        {/* Desktop/Tablet Chart */}
        <div className="emission-chart desktop-chart">
          <div className="chart-bars">
            {emissionData.map((item, index) => (
              <div className="chart-bar" key={index}>
                <div className="bar-fill" style={{ height: `${item.height}%` }}></div>
                <div className="bar-value" data-short={item.short}>
                  <span>{item.total.split(' ')[0]}</span>
                </div>
                <div className="bar-label">
                  {item.year.split(' ')[0]}<br/>({item.percentage}%)
                </div>
              </div>
            ))}
          </div>
          <div className="chart-note">Remaining 10% (40M AFI) halves each year, following Bitcoin's emission curve</div>
        </div>
        
        {/* Mobile-optimized Chart for very small screens */}
        <div className="mobile-chart">
          <ul className="mobile-emission-list">
            {emissionData.map((item, index) => (
              <li className="mobile-emission-item" key={index}>
                <span className="year">{item.year} ({item.percentage}%)</span>
                <div className="bar-container">
                  <div className="mobile-bar" style={{ width: `${item.height}%` }}></div>
                  <span className="value">{item.short} AFI</span>
                </div>
              </li>
            ))}
          </ul>
          <div className="chart-note">Remaining 10% (40M AFI) halves each year, following Bitcoin's emission curve</div>
        </div>
        
        {/* Responsive Table */}
        <div className="emission-table-wrapper">
          <table className="emission-table">
            <thead>
              <tr>
                <th>Year</th>
                <th>Pool Allocation</th>
                <th>Total AFI</th>
                <th>Daily Maximum</th>
              </tr>
            </thead>
            <tbody>
              {emissionData.map((item, index) => {
                // Handle the special case for Year 5+
                if (item.year === "Year 5+") {
                  return (
                    <tr key={index}>
                      <td>{item.year}</td>
                      <td colSpan="3">Remaining 10% (40M AFI) halves each year, following Bitcoin's emission curve</td>
                    </tr>
                  );
                }
                
                return (
                  <tr key={index}>
                    <td>{item.year}</td>
                    <td>{item.percentage}%</td>
                    <td>{item.total}</td>
                    <td className={item.dailyTooltip ? "tooltipped" : ""}>
                      {item.daily}
                      {item.dailyTooltip && (
                        <span className="tooltip">{item.dailyTooltip}</span>
                      )}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </div>
    );
  };

  return (
    <section id="tokenomics" className="rwa-explanation">
      <div className="container">
        <div className="glass-card">
          <h2 className="section-title">Aledger <span>Tokenomics</span></h2>
          <p>The AeroFi ecosystem is built on the AFI token, with a fixed total supply of 1 billion tokens. Our economic model balances mining rewards with actual business revenue to create sustainable long-term growth.</p>
          
          <div className="token-economics">
            <h3>Token Supply & Distribution</h3>
            <p>The total supply of 1 billion AFI tokens is strategically allocated to ensure ecosystem sustainability:</p>
            
            {/* Donut Chart for Token Distribution */}
            <div className="token-distribution-chart">
              <div className="chart-container">
                <DonutChart data={tokenDistribution} />
              </div>
              
              <div className="chart-legend">
                {tokenDistribution.map((item, index) => (
                  <div className="legend-item" key={index}>
                    <div className="legend-color" style={{backgroundColor: item.color}}></div>
                    <div className="legend-text">
                      <strong>{item.percentage}%</strong> - {item.description}
                      {item.note && <span className="token-note"> {item.note}</span>}
                    </div>
                  </div>
                ))}
              </div>
            </div>
            
            {/* Responsive Emission Schedule */}
            <EmissionSchedule />
            
            <h3>Temporal Reward Adjustment Mechanism</h3>
            <p>Our innovative reward system ensures early adopters maintain a permanent advantage while keeping the system sustainable:</p>
            
            <div className="reward-formula">
              <div className="formula-equation">
                User Reward = Base Reward × [α × Temporal Coefficient + β × Staking Ratio + γ × Network Saturation Factor]
              </div>
              
              {/* Visual representation of the formula components */}
              <div className="formula-visualization">
                <div className="formula-component">
                  <div className="component-icon temporal-icon">
                    <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                      <circle cx="12" cy="12" r="9" stroke="currentColor" strokeWidth="2"/>
                      <path d="M12 7V12L15 15" stroke="currentColor" strokeWidth="2" strokeLinecap="round"/>
                    </svg>
                  </div>
                  <div className="component-info">
                    <h5>Temporal Coefficient (α)</h5>
                    <p>Rewards early adoption with up to 1.5× multiplier</p>
                  </div>
                </div>
                
                <div className="formula-component">
                  <div className="component-icon staking-icon">
                    <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                      <path d="M12 2L2 7L12 12L22 7L12 2Z" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                      <path d="M2 17L12 22L22 17" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                      <path d="M2 12L12 17L22 12" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                    </svg>
                  </div>
                  <div className="component-info">
                    <h5>Staking Ratio (β)</h5>
                    <p>Increases rewards based on your staking commitment</p>
                  </div>
                </div>
                
                <div className="formula-component">
                  <div className="component-icon network-icon">
                    <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                      <path d="M4 4L20 20" stroke="currentColor" strokeWidth="2" strokeLinecap="round"/>
                      <path d="M12 4C14.1217 4 16.1566 4.84285 17.6569 6.34315C19.1571 7.84344 20 9.87827 20 12C20 14.1217 19.1571 16.1566 17.6569 17.6569C16.1566 19.1571 14.1217 20 12 20C9.87827 20 7.84344 19.1571 6.34315 17.6569C4.84285 16.1566 4 14.1217 4 12" stroke="currentColor" strokeWidth="2" strokeLinecap="round"/>
                    </svg>
                  </div>
                  <div className="component-info">
                    <h5>Network Saturation Factor (γ)</h5>
                    <p>Adjusts as network approaches maximum emission capacity</p>
                  </div>
                </div>
              </div>
            </div>
            
            <p className="token-note" style={{ marginTop: "1rem" }}>This mechanism creates natural FOMO while ensuring early participants always maintain their advantage, even as the network grows.</p>
          </div>
          
          <div className="warning-box">
            <h4>Important Token-Denominated Returns Note</h4>
            <p>All reward calculations in the AeroFi ecosystem are conducted on a token-denominated basis. This means rewards are based on AFI token quantities rather than USD values, ensuring system stability despite token price fluctuations. Actual yields in USD terms will adjust dynamically according to the protocol's real revenue and market conditions.</p>
          </div>
        </div>
      </div>
    </section>
  );
};

export default Tokenomics;
